import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const IncidentPopup = ({ incident, onClose, position = { x: 0, y: 0 } }) => {
  if (!incident) return null;

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'text-error bg-error/10 border-error/20';
      case 'major': return 'text-warning bg-warning/10 border-warning/20';
      case 'minor': return 'text-accent bg-accent/10 border-accent/20';
      case 'resolved': return 'text-success bg-success/10 border-success/20';
      default: return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getServiceIcon = (serviceType) => {
    switch (serviceType) {
      case 'internet': return 'Wifi';
      case 'mobile': return 'Smartphone';
      case 'streaming': return 'Play';
      case 'cloud': return 'Cloud';
      case 'financial': return 'CreditCard';
      case 'gaming': return 'Gamepad2';
      default: return 'AlertCircle';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return 'AlertTriangle';
      case 'resolved': return 'CheckCircle';
      case 'investigating': return 'Search';
      default: return 'Clock';
    }
  };

  const formatDuration = (duration) => {
    return duration?.replace(/(\d+)h\s*(\d+)m/, '$1h $2m');
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-50 bg-black/20" 
        onClick={onClose}
      />
      {/* Popup */}
      <div 
        className="fixed z-50 bg-card border border-border rounded-lg shadow-xl max-w-sm w-full mx-4"
        style={{
          left: `${Math.min(position?.x, window.innerWidth - 400)}px`,
          top: `${Math.min(position?.y, window.innerHeight - 300)}px`,
          transform: position?.x > window.innerWidth - 400 ? 'translateX(-100%)' : 'none'
        }}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-4 border-b border-border">
          <div className="flex items-start space-x-3 flex-1">
            <div className={`flex items-center justify-center w-10 h-10 rounded-full border ${getSeverityColor(incident?.severity)}`}>
              <Icon name={getServiceIcon(incident?.serviceType)} size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-1">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${getSeverityColor(incident?.severity)}`}>
                  {incident?.severity?.toUpperCase()}
                </span>
                <Icon 
                  name={getStatusIcon(incident?.status)} 
                  size={14} 
                  className={incident?.status === 'active' ? 'text-error' : 'text-success'} 
                />
              </div>
              <h3 className="text-sm font-semibold text-foreground line-clamp-2">
                {incident?.title}
              </h3>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <Icon name="X" size={16} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Provider Info */}
          <div className="flex items-center space-x-3">
            <Image
              src={`https://logo.clearbit.com/${incident?.provider?.toLowerCase()?.replace(/\s+/g, '')}.com`}
              alt={`${incident?.provider} company logo`}
              className="w-8 h-8 rounded"
            />
            <div>
              <p className="text-sm font-medium text-foreground">
                {incident?.provider}
              </p>
              <p className="text-xs text-muted-foreground capitalize">
                {incident?.serviceType?.replace(/([A-Z])/g, ' $1')?.trim()} Service
              </p>
            </div>
          </div>

          {/* Description */}
          <p className="text-sm text-muted-foreground">
            {incident?.description}
          </p>

          {/* Metrics Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center justify-center space-x-1 mb-1">
                <Icon name="Users" size={14} className="text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Affected</span>
              </div>
              <p className="text-sm font-semibold text-foreground">
                {incident?.affectedUsers?.toLocaleString()}
              </p>
            </div>
            
            <div className="text-center p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center justify-center space-x-1 mb-1">
                <Icon name="Clock" size={14} className="text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Duration</span>
              </div>
              <p className="text-sm font-semibold text-foreground">
                {formatDuration(incident?.duration)}
              </p>
            </div>
          </div>

          {/* Location Info */}
          <div className="flex items-center space-x-2 p-3 bg-muted/30 rounded-lg">
            <Icon name="MapPin" size={16} className="text-muted-foreground" />
            <div>
              <p className="text-xs text-muted-foreground">Location</p>
              <p className="text-sm font-medium text-foreground">
                {incident?.lat?.toFixed(4)}, {incident?.lng?.toFixed(4)}
              </p>
            </div>
          </div>

          {/* Real-time Updates */}
          <div className="flex items-center justify-between p-3 bg-primary/5 border border-primary/20 rounded-lg">
            <div className="flex items-center space-x-2">
              <div className="status-indicator status-online"></div>
              <span className="text-xs text-primary font-medium">Live Updates</span>
            </div>
            <span className="text-xs text-muted-foreground">2 min ago</span>
          </div>
        </div>

        {/* Actions */}
        <div className="p-4 border-t border-border">
          <div className="flex space-x-2">
            <Button variant="outline" size="sm" className="flex-1" asChild>
              <Link to="/service-outage-details">
                <Icon name="ExternalLink" size={14} className="mr-2" />
                View Details
              </Link>
            </Button>
            <Button variant="default" size="sm" className="flex-1" asChild>
              <Link to="/report-outage">
                <Icon name="Plus" size={14} className="mr-2" />
                Report Issue
              </Link>
            </Button>
          </div>
          
          {/* Quick Actions */}
          <div className="flex items-center justify-center space-x-4 mt-3 pt-3 border-t border-border">
            <button className="flex items-center space-x-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
              <Icon name="ThumbsUp" size={12} />
              <span>Confirm (234)</span>
            </button>
            <button className="flex items-center space-x-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
              <Icon name="MessageCircle" size={12} />
              <span>Comments (45)</span>
            </button>
            <button className="flex items-center space-x-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
              <Icon name="Share2" size={12} />
              <span>Share</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default IncidentPopup;